/**
 * SaveQueueManager - Unified manager for Background Cloud Save Queue
 *
 * Integrates SaveQueue, Debouncer, and FlushScheduler into a single
 * cohesive interface for the editor to use.
 *
 * @module features/storage/saveQueueManager
 * @see SPEC-006-background-cloud-save-queue
 * @see Requirements 2.1, 2.2, 3.6, 8.1-8.6, 10.7
 */

import { SaveQueue } from './saveQueue';
import { Debouncer, DEFAULT_DEBOUNCE_DELAY_MS } from './debouncer';
import { FlushScheduler } from './flushScheduler';
import { DocApiClient } from './docApiClient';
import type { PendingSave, SaveStatus, SaveQueueState, FlushResult, FlushError } from './types';

/**
 * Input for save operation (passed to debouncer)
 */
interface SaveInput {
  docId: string;
  markdown: string;
  baseVersionId: string;
  meta?: {
    title?: string;
  };
}

/**
 * Options for SaveQueueManager configuration
 */
export interface SaveQueueManagerOptions {
  /** Debounce delay in milliseconds (default: 2000) */
  debounceMs?: number;
  /** Maximum debounce wait time in milliseconds */
  maxDebounceWaitMs?: number;
  /** Callback when state changes */
  onStatusChange?: (state: SaveQueueState) => void;
  /** Callback when a version conflict is detected */
  onConflict?: (docId: string, serverVersionId: string) => void;
  /** Callback when an error occurs after all retries */
  onError?: (error: FlushError) => void;
  /** Callback when a save succeeds, provides new versionId */
  onSuccess?: (docId: string, newVersionId: string) => void;
}

/**
 * Default options
 */
const DEFAULT_OPTIONS: Required<
  Omit<SaveQueueManagerOptions, 'onStatusChange' | 'onConflict' | 'onError' | 'onSuccess'>
> = {
  debounceMs: DEFAULT_DEBOUNCE_DELAY_MS,
  maxDebounceWaitMs: 10000, // Force save after 10 seconds of continuous editing
};

/**
 * SaveQueueManager - Unified manager for background save operations
 *
 * Features:
 * - Integrates SaveQueue, Debouncer, and FlushScheduler
 * - Provides unified state management
 * - Exposes simple API for editor integration
 * - Handles lifecycle management
 *
 * @see Requirements 2.1, 2.2, 8.1-8.6
 *
 * @example
 * ```typescript
 * const manager = new SaveQueueManager(queue, scheduler, apiClient, {
 *   onStatusChange: (state) => updateUI(state),
 *   onConflict: (docId, serverVersion) => showConflictDialog(docId),
 * });
 *
 * await manager.init();
 *
 * // Called by editor on change
 * manager.save(docId, markdown, baseVersionId);
 *
 * // Cleanup on unmount
 * manager.destroy();
 * ```
 */
export class SaveQueueManager {
  private debouncer: Debouncer<SaveInput> | null = null;
  private state: SaveQueueState;
  private isInitialized: boolean = false;
  private boundOnOnline: () => void;
  private boundOnOffline: () => void;

  // Resolved options
  private readonly debounceMs: number;
  private readonly maxDebounceWaitMs: number;

  constructor(
    private readonly queue: SaveQueue,
    private readonly scheduler: FlushScheduler,
    private readonly options: SaveQueueManagerOptions = {}
  ) {
    this.debounceMs = options.debounceMs ?? DEFAULT_OPTIONS.debounceMs;
    this.maxDebounceWaitMs = options.maxDebounceWaitMs ?? DEFAULT_OPTIONS.maxDebounceWaitMs;

    // Initialize state
    this.state = {
      status: 'saved',
      queueLength: 0,
      lastSavedAt: null,
      lastError: null,
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    };

    // Bind event handlers
    this.boundOnOnline = this.handleOnline.bind(this);
    this.boundOnOffline = this.handleOffline.bind(this);
  }

  /**
   * Initialize the SaveQueueManager
   *
   * Sets up the debouncer, starts the flush scheduler, and restores
   * any pending saves from IndexedDB.
   *
   * @see Requirements 1.5 - Restore pending saves from IndexedDB
   */
  async init(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    // Initialize the queue (opens IndexedDB)
    await this.queue.init();

    // Create debouncer
    this.debouncer = new Debouncer<SaveInput>((input) => this.handleDebouncedSave(input), {
      delayMs: this.debounceMs,
      maxWaitMs: this.maxDebounceWaitMs,
    });

    // Add network event listeners
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.boundOnOnline);
      window.addEventListener('offline', this.boundOnOffline);
    }

    // Start the flush scheduler
    this.scheduler.start();

    // Update initial state based on queue contents
    await this.updateState();

    this.isInitialized = true;
  }

  /**
   * Save document content (editor entry point)
   *
   * This is the main method called by the editor on content changes.
   * The save is debounced to batch rapid edits.
   *
   * @param docId - Document ID to save
   * @param markdown - Full markdown content
   * @param baseVersionId - Current version ID for optimistic locking
   * @param meta - Optional metadata updates (e.g., title)
   *
   * @see Requirements 2.1 - Debounce save requests with 2-second delay
   * @see Requirements 2.2 - Only enqueue final state within debounce window
   */
  save(docId: string, markdown: string, baseVersionId: string, meta?: { title?: string }): void {
    if (!this.isInitialized || !this.debouncer) {
      console.warn('[SaveQueueManager] Not initialized. Call init() first.');
      return;
    }

    // Update status to pending while debouncing
    this.updateStatus('pending');

    // Debounce the save
    this.debouncer.call({ docId, markdown, baseVersionId, meta });
  }

  /**
   * Handle debounced save - enqueue to SaveQueue
   *
   * Called after debounce delay expires with the final state.
   */
  private async handleDebouncedSave(input: SaveInput): Promise<void> {
    const { docId, markdown, baseVersionId, meta } = input;

    try {
      // Enqueue the save
      await this.queue.enqueue({ docId, markdown, baseVersionId, meta });

      // Update state
      await this.updateState();

      // Trigger flush
      this.scheduler.scheduleFlush();
    } catch (error) {
      console.error('[SaveQueueManager] Failed to enqueue save:', error);
      this.updateStatus('error', error instanceof Error ? error.message : 'Failed to enqueue save');
    }
  }

  /**
   * Manually trigger immediate flush
   *
   * @param docId - Optional document ID to flush only saves for that document
   * @returns Array of flush results
   *
   * @see Requirements 3.6 - Support manual retry via "Retry Now" button
   */
  async retryNow(docId?: string): Promise<FlushResult[]> {
    if (!this.isInitialized) {
      console.warn('[SaveQueueManager] Not initialized. Call init() first.');
      return [];
    }

    // Reset failed saves to pending so they can be retried
    const saves = await this.queue.getAll(docId);
    for (const save of saves) {
      if (save.status === 'failed') {
        await this.queue.resetAttempt(save.saveId);
      }
    }

    // Trigger immediate flush
    const results = await this.scheduler.flushNow(docId);

    // Update state after flush
    await this.updateState();

    return results;
  }

  /**
   * Clear all pending saves from the queue
   *
   * @param docId - Optional document ID to clear only saves for that document
   *
   * @see Requirements 7.6 - Provide "Clear Queue" button for debugging
   */
  async clearQueue(docId?: string): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[SaveQueueManager] Not initialized. Call init() first.');
      return;
    }

    // Cancel any pending debounced save
    this.debouncer?.cancel();

    // Clear the queue
    await this.queue.clear(docId);

    // Update state
    await this.updateState();
  }

  /**
   * Export all pending saves for debugging or data recovery
   *
   * @returns Array of all pending saves
   *
   * @see Requirements 10.7 - Provide data export option when queue is stuck
   */
  async exportQueue(): Promise<PendingSave[]> {
    if (!this.isInitialized) {
      console.warn('[SaveQueueManager] Not initialized. Call init() first.');
      return [];
    }

    return this.queue.getAll();
  }

  /**
   * Get current state
   *
   * @returns Current SaveQueueState
   *
   * @see Requirements 8.1-8.6
   */
  getState(): SaveQueueState {
    return { ...this.state };
  }

  /**
   * Get all queue items for a specific document
   *
   * @param docId - Optional document ID to filter by
   * @returns Array of pending saves
   */
  async getQueueItems(docId?: string): Promise<PendingSave[]> {
    if (!this.isInitialized) {
      return [];
    }

    return this.queue.getAll(docId);
  }

  /**
   * Check if there are any pending saves
   */
  hasPendingSaves(): boolean {
    return this.state.queueLength > 0 || (this.debouncer?.isPending() ?? false);
  }

  /**
   * Flush any pending debounced save immediately
   *
   * Useful before page unload to ensure no data is lost.
   */
  flushDebouncer(): void {
    this.debouncer?.flush();
  }

  /**
   * Destroy the SaveQueueManager
   *
   * Stops the scheduler, cancels pending operations, and cleans up resources.
   */
  destroy(): void {
    if (!this.isInitialized) {
      return;
    }

    // Cancel debouncer
    this.debouncer?.cancel();
    this.debouncer = null;

    // Stop scheduler
    this.scheduler.stop();

    // Remove network event listeners
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.boundOnOnline);
      window.removeEventListener('offline', this.boundOnOffline);
    }

    // Close queue connection
    this.queue.close();

    this.isInitialized = false;
  }

  /**
   * Update internal state and notify listeners
   *
   * Calculates the current status based on queue state and network status.
   */
  private async updateState(): Promise<void> {
    const queueLength = await this.queue.getQueueLength();
    const saves = await this.queue.getAll();

    // Determine status
    let status: SaveStatus;
    let lastError: string | null = null;

    if (!this.state.isOnline) {
      status = 'offline';
    } else if (saves.some((s) => s.status === 'failed')) {
      status = 'error';
      const failedSave = saves.find((s) => s.status === 'failed');
      lastError = failedSave?.lastError ?? 'Save failed';
    } else if (saves.some((s) => s.status === 'flushing')) {
      status = 'saving';
    } else if (queueLength > 0 || this.debouncer?.isPending()) {
      status = 'pending';
    } else {
      status = 'saved';
    }

    // Update state
    const newState: SaveQueueState = {
      status,
      queueLength,
      lastSavedAt: this.state.lastSavedAt,
      lastError,
      isOnline: this.state.isOnline,
    };

    // Check if state changed
    const stateChanged =
      this.state.status !== newState.status ||
      this.state.queueLength !== newState.queueLength ||
      this.state.lastError !== newState.lastError ||
      this.state.isOnline !== newState.isOnline;

    this.state = newState;

    // Notify listeners if state changed
    if (stateChanged) {
      this.options.onStatusChange?.(this.getState());
    }
  }

  /**
   * Update status directly (for immediate UI feedback)
   */
  private updateStatus(status: SaveStatus, error?: string): void {
    const stateChanged = this.state.status !== status || this.state.lastError !== (error ?? null);

    this.state = {
      ...this.state,
      status,
      lastError: error ?? null,
    };

    if (stateChanged) {
      this.options.onStatusChange?.(this.getState());
    }
  }

  /**
   * Handle successful flush - update lastSavedAt
   *
   * Called by FlushScheduler via callback.
   */
  handleFlushComplete(result: FlushResult): void {
    if (result.success) {
      this.state.lastSavedAt = Date.now();
      // Directly set status to 'saved' on success to avoid stale failed saves in IndexedDB
      this.updateStatus('saved');
    } else {
      // Update state asynchronously for failures
      this.updateState().catch((err) => {
        console.error('[SaveQueueManager] Failed to update state:', err);
      });
    }
  }

  /**
   * Handle flush error
   *
   * Called by FlushScheduler via callback.
   */
  handleFlushError(error: FlushError): void {
    this.options.onError?.(error);

    // Update state asynchronously
    this.updateState().catch((err) => {
      console.error('[SaveQueueManager] Failed to update state:', err);
    });
  }

  /**
   * Handle conflict detection
   *
   * Called by FlushScheduler via callback.
   */
  handleConflict(docId: string, serverVersionId: string): void {
    this.options.onConflict?.(docId, serverVersionId);

    // Update state asynchronously
    this.updateState().catch((err) => {
      console.error('[SaveQueueManager] Failed to update state:', err);
    });
  }

  /**
   * Handle online event
   */
  private handleOnline(): void {
    this.state.isOnline = true;
    this.updateState().catch((err) => {
      console.error('[SaveQueueManager] Failed to update state:', err);
    });
  }

  /**
   * Handle offline event
   */
  private handleOffline(): void {
    this.state.isOnline = false;
    this.updateStatus('offline');
  }
}

/**
 * Factory function to create a fully configured SaveQueueManager
 *
 * Creates and wires up all dependencies (SaveQueue, FlushScheduler, DocApiClient).
 *
 * @param baseUrl - Base URL for the API
 * @param getAuthToken - Function to get the current auth token
 * @param options - SaveQueueManager options
 * @returns Configured SaveQueueManager instance
 *
 * @example
 * ```typescript
 * const manager = createSaveQueueManager(
 *   'https://api.linkode.app',
 *   () => getAuthToken(),
 *   {
 *     onStatusChange: (state) => updateUI(state),
 *     onConflict: (docId) => showConflictDialog(docId),
 *   }
 * );
 *
 * await manager.init();
 * ```
 */
export function createSaveQueueManager(
  baseUrl: string,
  getAuthToken: () => Promise<string>,
  options: SaveQueueManagerOptions = {}
): SaveQueueManager {
  // Create dependencies
  const queue = new SaveQueue();
  const apiClient = new DocApiClient(baseUrl, getAuthToken);

  // Create manager first (we need reference for callbacks)
  const manager = new SaveQueueManager(queue, null as unknown as FlushScheduler, options);

  // Create scheduler with callbacks wired to manager
  const scheduler = new FlushScheduler(queue, apiClient, {
    onFlushComplete: (result) => manager.handleFlushComplete(result),
    onError: (error) => manager.handleFlushError(error),
    onConflict: (docId, serverVersionId) => manager.handleConflict(docId, serverVersionId),
    onSuccess: options.onSuccess,
  });

  // Replace the scheduler (a bit hacky but avoids circular dependency)
  // Using Object.assign to bypass private property restriction
  Object.assign(manager, { scheduler });

  return manager;
}
